import { useState, useEffect, createContext, useContext, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { setUserContext } from "@/lib/sentry";

interface Profile {
  id: string;
  user_id: string;
  email: string;
  display_name: string | null;
  credits: number;
  created_at: string;
  last_login: string | null;
  is_suspended?: boolean;
  credits_expires_at?: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  isSuspended: boolean;
  signUp: (email: string, password: string, displayName?: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSuspended, setIsSuspended] = useState(false);

  const fetchProfile = async (userId: string) => {
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("user_id", userId)
      .maybeSingle();

    if (error) {
      console.error("Error fetching profile:", error);
      return null;
    }
    return data as Profile | null;
  };

  const updateLastLogin = async (userId: string) => {
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ last_login: new Date().toISOString() })
        .eq("user_id", userId);

      if (error) console.warn("Failed to update last_login:", error);
    } catch (e) {
      console.warn("Failed to update last_login:", e);
    }
  };

  const refreshProfile = async () => {
    if (user) {
      const profileData = await fetchProfile(user.id);
      setProfile(profileData);
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        // Clean up OAuth hash from URL after successful sign in
        if (event === 'SIGNED_IN' && window.location.hash.includes('access_token')) {
          window.history.replaceState({}, document.title, window.location.pathname);
        }

        // Defer profile fetch with setTimeout to avoid deadlocks
        if (session?.user) {
          // Set Sentry user context
          setUserContext({
            id: session.user.id,
            email: session.user.email,
          });
          setTimeout(() => {
            fetchProfile(session.user.id).then((profileData) => {
              if (profileData?.is_suspended) {
                setIsSuspended(true);
                setProfile(null);
              } else {
                setIsSuspended(false);
                setProfile(profileData);
                updateLastLogin(session.user.id);
              }
            });
          }, 0);
        } else {
          // Clear Sentry user context
          setUserContext(null);
          setProfile(null);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        // Set Sentry user context for existing session
        setUserContext({
          id: session.user.id,
          email: session.user.email,
        });
        fetchProfile(session.user.id).then((profileData) => {
          if (profileData?.is_suspended) {
            setIsSuspended(true);
            setProfile(null);
          } else {
            setIsSuspended(false);
            setProfile(profileData);
            updateLastLogin(session.user.id);
          }
        });
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, displayName?: string) => {
    const redirectUrl = `${window.location.origin}/`;

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          display_name: displayName || email.split("@")[0],
        },
      },
    });

    return { error: error as Error | null };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    return { error: error as Error | null };
  };

  const signInWithGoogle = async () => {
    const redirectUrl = `${window.location.origin}/dashboard`;

    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: redirectUrl,
      },
    });

    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUserContext(null); // Clear Sentry user context
    setProfile(null);
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      profile,
      loading,
      isSuspended,
      signUp,
      signIn,
      signInWithGoogle,
      signOut,
      refreshProfile,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
